<template>
  <div class="sidebar-left">
    <div class="sidebar">

      <!-- Logged In User Profile Sidebar -->
      <user-profile-sidebar
        :shall-show-user-profile-sidebar="shallShowUserProfileSidebar"
        :profile-user-data="profileUserData"
        @close-sidebar="$emit('update:shall-show-user-profile-sidebar', false)"
      />

      <!-- Sidebar Content -->
      <div
        class="sidebar-content"
        :class="{'show': mqShallShowLeftSidebar}"
      >

        <!-- Sidebar close icon -->
        <span class="sidebar-close-icon">
          <feather-icon
            icon="XIcon"
            size="16"
            @click="$emit('update:mq-shall-show-left-sidebar', false)"
          />
        </span>

        <!-- Header -->
        <div class="px-2 pt-1">
          <b-badge
            :variant="activeFilter === 'unread' ? 'danger' : 'light-primary'"
            class="px-1 mr-1 cursor-pointer"
            @click="filter_contact('unread')"
          >未讀取</b-badge>
          <b-badge
            :variant="activeFilter === 'unreplied' ? 'danger' : 'light-primary'"
            class="px-1 mr-1 cursor-pointer"
            @click="filter_contact('unreplied')"
          >未回覆</b-badge>
        </div>
        <div class="chat-fixed-search">
          <div class="d-flex align-items-center w-100">
            <!-- Search -->
            <b-input-group class="input-group-merge ml-1 w-100 round">
              <b-input-group-prepend is-text>
                <feather-icon
                  icon="SearchIcon"
                  class="text-muted"
                />
              </b-input-group-prepend>
              <b-form-input
                v-model="searchQuery"
                placeholder="Search..."
              />
            </b-input-group>

            <b-button
              variant="primary"
          size="sm"
              class="ml-1 add-chat-btn"
              v-b-modal.modal-login
            >
              <feather-icon icon="PlusIcon" size="18" />
            </b-button>
          </div>
        </div>
        
        <b-modal
          id="modal-login"
          cancel-variant="outline-secondary"
          centered
          title="New Chat"
          :ok-disabled="!mobile_number || isSendingNewChat"
          :busy="isSendingNewChat"
        >
          <b-form>
            <b-form-group>
              <label for="email">Phone Number:</label>
              <div class="d-flex">
                <div class="country-code-select mr-1">
                  <v-select
                    v-model="country_code"
                    :options="countryCodeOptions"
                    :clearable="false"
                    :searchable="true"
                    :reduce="option => option.value"
                    label="label"
                  />
                </div>
                <b-form-input
                  id="email"
                  type="text"
                  inputmode="numeric"
                  placeholder="91234567"
                  v-model="mobile_number"
                
                />
              </div>
            </b-form-group>
            <br>
            <b-form-textarea
              id="message"
              placeholder="訊息內容"
              rows="3"
              max-rows="8"
              v-model="message"
              />
          </b-form>

          <template #modal-footer="{ cancel }">
            <b-button
              variant="outline-secondary"
              :disabled="isSendingNewChat"
              @click="cancel()"
            >
              Close
            </b-button>
            <b-button
              variant="primary"
              :disabled="!mobile_number || isSendingNewChat"
              @click="AddNewChat"
            >
              <font-awesome-icon v-if="isSendingNewChat" icon="fa-solid fa-spinner" spin class="mr-50" />
              {{ isSendingNewChat ? 'Sending...' : 'Send' }}
            </b-button>
          </template>
        </b-modal>

        <!-- ScrollArea: Chat & Contacts -->
        <vue-perfect-scrollbar
          ref="scrollRootEl"
          :settings="perfectScrollbarSettings"
          class="chat-user-list-wrapper list-group scroll-area"
        >

          <!-- Chats Title -->


          <ul class="chat-users-list chat-list media-list">
            <chat-contact
              v-for="contact in filteredChatsContacts"
              :key="contactKey(contact)"
              :user="contact"
              tag="li"
              is-chat-contact
              @click="$emit('open-chat', contact)"
            />
            <li ref="sentinelEl" class="py-1 text-center">
              <small v-if="isLoading">Loading…</small>
              <small v-else-if="showNoMore">No more</small>
            </li>
          </ul>

        </vue-perfect-scrollbar>
      </div>

    </div>
  </div>
</template>

<script>
import {
  BAvatar, BInputGroup, BInputGroupPrepend, BFormInput, BButton, BBadge, BForm, BFormGroup, BFormTextarea
} from 'bootstrap-vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { ref, onMounted, onBeforeUnmount, nextTick, computed, getCurrentInstance, watch } from '@vue/composition-api'
import ChatContact from './ChatContact.vue'
import UserProfileSidebar from './UserProfileSidebar.vue'
import vSelect from 'vue-select'

export default {
  components: {
    // BSV
    BAvatar,
    BBadge,
    BFormTextarea,
    BInputGroup,
    BButton,
    BInputGroupPrepend,
    BFormInput,
    BForm,
    BFormGroup,
    vSelect,

    // 3rd party
    VuePerfectScrollbar,

    // SFC
    ChatContact,
    UserProfileSidebar,
  },
  props: {
    chatsContacts: {
      type: Array,
      required: true,
    },
    shallShowUserProfileSidebar: {
      type: Boolean,
      required: true,
    },
    profileUserData: {
      type: Object,
      required: true,
    },
    activeChatContactId: {
      type: Number,
      default: null,
    },
    mqShallShowLeftSidebar: {
      type: Boolean,
      required: true,
    },
    hasMore: { type: Boolean, default: true },
    isLoading: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance()?.proxy
    const perfectScrollbarSettings = {
      maxScrollbarLength: 150,
    }

    const resolveChatContact = userId => props.chatsContacts.find(contact => contact.id === userId)

    // Search Query
    const searchQuery = ref('')
    let searchTimer = null
    watch(searchQuery, value => {
      clearTimeout(searchTimer)
      searchTimer = setTimeout(() => {
        emit('search-mobile', normalize(value))
      }, 400)
    })
    const filteredChatsContacts = computed(() => {
      const q = normalize(searchQuery.value)
      if (!q) return props.chatsContacts
      return props.chatsContacts.filter(c => {
        const mobile = normalize(c.contact_number || c.mobile)
        const code = normalize(c.member && c.member.code)
        const name = String((c.member && (c.member.zh_name || c.member.en_name)) || c.display_name || '').toLowerCase()
        const queryText = String(searchQuery.value || '').toLowerCase()
        return mobile.includes(q) || code.includes(q) || name.includes(queryText)
      })
    })
    const showNoMore = computed(() => !props.isLoading && !props.hasMore && props.chatsContacts.length > 0)
    //const filteredContacts = computed(() => props.contacts.filter(searchFilterFunction))
    const scrollRootEl = ref(null)   // your scroll container
    const sentinelEl = ref(null)
    let observer = null
    const countryCodeOptions = [
      { label: '🇭🇰 852', value: '852' },
      { label: '🇲🇴 853', value: '853' },
      { label: '🇨🇳 86', value: '86' },
      { label: '🇸🇬 65', value: '65' },
      { label: '🇲🇾 60', value: '60' },
      { label: '🇹🇼 886', value: '886' },
      { label: '🇯🇵 81', value: '81' },
      { label: '🇰🇷 82', value: '82' },
      { label: '🇺🇸 1', value: '1' },
      { label: '🇬🇧 44', value: '44' },
      { label: '🇦🇺 61', value: '61' },
    ]
    const country_code = ref('852')
    const mobile_number = ref('')
    const isSendingNewChat = ref(false)
    const message = ref('')
    const activeFilter = ref('')
    const normalizeDigits = v => String(v || '').replace(/\D/g, '')
    
    
    onMounted(async () => {
      await nextTick()

      // ✅ for vue-perfect-scrollbar, the scrollable element is usually its $el
      const rootEl =
            scrollRootEl.value?.$el?.querySelector('.ps') ||
            scrollRootEl.value?.$el ||
            scrollRootEl.value

      observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) {
            if (!props.isLoading && props.hasMore) emit('load-more')
          }
        },
        {
          root: rootEl,
          rootMargin: '200px',
          threshold: 0,
        }
      )
      if (sentinelEl.value) observer.observe(sentinelEl.value)
    })

    onBeforeUnmount(() => {
      if (observer) observer.disconnect()
      clearTimeout(searchTimer)
    })

    const normalize = v => String(v || '').replace(/\D/g, '')

    const contactKey = contact => {
      const base = contact.id || contact.student_id || contact.contact_number || contact.to_number || contact.from_number || 'contact'
      const ts = contact.last_time || ''
      const unread = contact.unread_count || 0
      return `${base}-${ts}-${unread}`
    }

    function filter_contact(type) {
      if (type === 'unread' || type === 'unreplied') {
        activeFilter.value = activeFilter.value === type ? '' : type
      } else {
        activeFilter.value = ''
      }
      emit('filter-contact', activeFilter.value)
    }

    async function AddNewChat(){
      if (!vm || !vm.$http || !mobile_number.value || isSendingNewChat.value) return

      isSendingNewChat.value = true

      const payload = {
        country_code: country_code.value,
        recipient: mobile_number.value,
        text_message: message.value,
        by_admin: "cloudfit",
      }

      try {
        const response = await vm.$http.post(
          process.env.VUE_APP_API_BASE + '/whatsapp/send-engagement-template/',
          payload,
        )

        if (response.status === 200 && response?.data?.result !== false) {
          mobile_number.value = ''
          country_code.value = '852'
          message.value = ''
          if (vm.$bvModal) {
            vm.$bvModal.hide('modal-login')
          }
        }
      } finally {
        isSendingNewChat.value = false
      }
    }

    return {
      // Search Query
      searchQuery,
      filteredChatsContacts,
      showNoMore,
      contactKey,
      //filteredContacts,
      scrollRootEl,
      sentinelEl,
      // UI
      resolveChatContact,
      perfectScrollbarSettings,
      countryCodeOptions,
      country_code,
      mobile_number,
      message,
      activeFilter,
      filter_contact,
      isSendingNewChat,
      AddNewChat
    }
  }
}
</script>

<style scoped>
.country-code-select {
  min-width: 120px;
}
</style>
